import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext, useApi, User } from '@repo-level/core-front';

import { CmsStardustRush, stardustRush } from 'api/cms/stardustRush';
import {
  calculateUserProgress,
  getStardustRushStaticData,
  getStardustRushStatus,
  getUserCompointsBySDRGroup,
  getUserTarget,
  stardustRushStaticDataMap,
  StardustRushStaticData,
  StardustRushStatus,
} from 'helpers/stardustRush';
import { isPromoAvailableDate } from 'helpers/isDateRange';
import { USER_GROUP } from 'helpers/user';

export interface StardustRushData {
  stardustRushCMSData?: CmsStardustRush | null;
  userTarget: number;
  userSDRProgressPercent: number;
  userCompointsBySDRGroup: number;
  stardustRushStatus?: StardustRushStatus;
  stardustRushStaticData?: StardustRushStaticData;
  isStardustRushAvailable: boolean;
}

const initialValues = {
  stardustRushCMSData: null,
  userSDRProgressPercent: 0,
  userTarget: 0,
  userCompointsBySDRGroup: 0,
  stardustRushStatus: StardustRushStatus.PVIP,
  stardustRushStaticData: stardustRushStaticDataMap[USER_GROUP.PVIP],
  isStardustRushAvailable: true,
};

export const StardustRushContext = createContext<StardustRushData>(null as any);

export const StardustRushContextProvider: FC = ({ children }) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const {
    i18n: { language },
  } = useTranslation();

  const [state, setState] = useState<StardustRushData>(initialValues);

  const [requestStardustRush, { data: stardustRushCMSData }] = useApi(
    stardustRush
  );

  const updateSDRData = useCallback(
    (
      { vip1Goal, vip2Goal, vip3Goal, startDate, endDate }: CmsStardustRush,
      user: User
    ) => {
      const { statuses } = user;

      const stardustRushStatus = getStardustRushStatus(user);
      const stardustRushStaticData = getStardustRushStaticData(statuses);

      const targets = { vip1Goal, vip2Goal, vip3Goal };

      const userTarget = getUserTarget(statuses, targets) || 0;

      const userCompointsBySDRGroup = getUserCompointsBySDRGroup(statuses);

      const userSDRProgressPercent =
        stardustRushStatus === StardustRushStatus.COMPLETE
          ? 100
          : calculateUserProgress(userCompointsBySDRGroup, userTarget);

      setState({
        stardustRushCMSData,
        userTarget,
        userSDRProgressPercent,
        userCompointsBySDRGroup,
        stardustRushStatus,
        stardustRushStaticData,
        isStardustRushAvailable: isPromoAvailableDate(startDate, endDate),
      });
    },
    [stardustRushCMSData]
  );

  useEffect(() => {
    requestStardustRush(language);
  }, [language, requestStardustRush]);

  useEffect(() => {
    !isAuthenticated && setState(initialValues);
  }, [isAuthenticated]);

  useEffect(() => {
    if (user && stardustRushCMSData) {
      updateSDRData(stardustRushCMSData, user);
    }
  }, [user, stardustRushCMSData, isAuthenticated, updateSDRData]);

  return (
    <StardustRushContext.Provider value={state}>
      {children}
    </StardustRushContext.Provider>
  );
};
